import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";

// User Pages
import HomePage from "./pages/HomePage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import DashboardPage from "./pages/DashboardPage";
import DepositPage from "./pages/DepositPage";
import WithdrawPage from "./pages/WithdrawPage";
import PortfolioPage from "./pages/PortfolioPage";
import ProfilePage from "./pages/ProfilePage";

// Admin Pages
import AdminLoginPage from "./pages/AdminLoginPage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import AdminUsersPage from "./pages/AdminUsersPage";
import AdminDepositsPage from "./pages/AdminDepositsPage";
import AdminWithdrawalsPage from "./pages/AdminWithdrawalsPage";
import AdminProfitsPage from "./pages/AdminProfitsPage";
import AdminSettingsPage from "./pages/AdminSettingsPage";
import AdminAuditPage from "./pages/AdminAuditPage";

// Loading Spinner Component
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animated-bg" />
    <div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
  </div>
);

// Protected Route - Redirects to login if not authenticated
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  if (!isAuthenticated || !user) {
    // Preserve the intended destination
    const returnTo = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?returnTo=${returnTo}`} replace />;
  }
  
  return children;
};

// Guest Route - Redirects to dashboard if already authenticated
const GuestRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  if (isAuthenticated && user) {
    // Check for returnTo parameter
    const params = new URLSearchParams(location.search);
    const returnTo = params.get('returnTo');
    const destination = returnTo ? decodeURIComponent(returnTo) : '/dashboard';
    return <Navigate to={destination} replace />;
  }
  
  return children;
};

// Admin Protected Route
const AdminProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, isAdmin } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  if (!isAuthenticated || !isAdmin) {
    const returnTo = encodeURIComponent(location.pathname);
    return <Navigate to={`/admin-login?returnTo=${returnTo}`} replace />;
  }
  
  return children;
};

// Admin Guest Route - Redirects to admin dashboard if already logged in as admin
const AdminGuestRoute = ({ children }) => {
  const { isAuthenticated, loading, isAdmin } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  if (isAuthenticated && isAdmin) {
    const params = new URLSearchParams(location.search);
    const returnTo = params.get('returnTo');
    const destination = returnTo ? decodeURIComponent(returnTo) : '/admin/dashboard';
    return <Navigate to={destination} replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/about" element={<HomePage />} />
      <Route path="/faq" element={<HomePage />} />
      
      {/* Guest Routes - Redirect to dashboard if authenticated */}
      <Route path="/login" element={
        <GuestRoute>
          <LoginPage />
        </GuestRoute>
      } />
      <Route path="/register" element={
        <GuestRoute>
          <RegisterPage />
        </GuestRoute>
      } />
      <Route path="/signup" element={
        <GuestRoute>
          <RegisterPage />
        </GuestRoute>
      } />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      
      {/* Protected User Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/deposit" element={
        <ProtectedRoute>
          <DepositPage />
        </ProtectedRoute>
      } />
      <Route path="/withdraw" element={
        <ProtectedRoute>
          <WithdrawPage />
        </ProtectedRoute>
      } />
      <Route path="/portfolio" element={
        <ProtectedRoute>
          <PortfolioPage />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      } />
      
      {/* Admin Routes */}
      <Route path="/admin-login" element={
        <AdminGuestRoute>
          <AdminLoginPage />
        </AdminGuestRoute>
      } />
      <Route path="/admin/dashboard" element={
        <AdminProtectedRoute>
          <AdminDashboardPage />
        </AdminProtectedRoute>
      } />
      <Route path="/admin/users" element={
        <AdminProtectedRoute>
          <AdminUsersPage />
        </AdminProtectedRoute>
      } />
      <Route path="/admin/deposits" element={
        <AdminProtectedRoute>
          <AdminDepositsPage />
        </AdminProtectedRoute>
      } />
      <Route path="/admin/withdrawals" element={
        <AdminProtectedRoute>
          <AdminWithdrawalsPage />
        </AdminProtectedRoute>
      } />
      <Route path="/admin/profits" element={
        <AdminProtectedRoute>
          <AdminProfitsPage />
        </AdminProtectedRoute>
      } />
      <Route path="/admin/settings" element={
        <AdminProtectedRoute>
          <AdminSettingsPage />
        </AdminProtectedRoute>
      } />
      <Route path="/admin/audit" element={
        <AdminProtectedRoute>
          <AdminAuditPage />
        </AdminProtectedRoute>
      } />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="App dark">
          <Toaster 
            position="top-right"
            toastOptions={{
              style: {
                background: '#0F1629',
                border: '1px solid rgba(255,255,255,0.1)',
                color: '#e2e8f0',
              },
              classNames: {
                success: 'border-emerald-500/30',
                error: 'border-red-500/30',
                info: 'border-cyan-500/30',
              }
            }}
          />
          <AppRoutes />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
